import React from 'react';
import { flexRender } from '@tanstack/react-table';
import { Virtualizer } from '@tanstack/react-virtual';
import { isSubtotalRow } from './table-utils';

import styles from './tanstack-table-styles.module.scss';

interface TableBodyProps {
  table: any;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  onRowClick?: (row: any) => void;
  selectedRowId?: string | null;
  subtotalConfig?: { columnIds: string[] };
  getSubtotalRow?: (rows: any[]) => Record<string, React.ReactNode>;
}

export const TableBody: React.FC<TableBodyProps> = ({
  table,
  virtualizer,
  onRowClick,
  selectedRowId,
  getSubtotalRow,
}) => {
  return (
    <tbody>
      <tr style={{ height: `${virtualizer.getTotalSize()}px` }}>
        <td
          colSpan={table.getVisibleLeafColumns().length || 1}
          style={{ padding: 0, border: 'none' }}
        />
      </tr>

      {virtualizer.getVirtualItems().map((virtualRow) => {
        const { rows } = table.getRowModel();
        const row = rows[virtualRow.index];

        return (
          <tr
            key={row.id}
            className={`${isSubtotalRow(row) ? styles.subtotalRow : ''} ${
              selectedRowId && row.original && row.original.id === selectedRowId
                ? styles.selectedRow
                : ''
            }`}
            onClick={() => {
              if (!isSubtotalRow(row) && onRowClick) {
                onRowClick(row.original);
              }
            }}
            style={{
              cursor: onRowClick ? 'pointer' : 'default',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '48px',
              transform: `translateY(${virtualRow.start}px)`,
              backgroundColor:
                virtualRow.index % 2 === 0 ? '#ffffff' : '#f4f4f4',
              transition: 'background-color 0.2s ease',
            }}
          >
            {row.getVisibleCells().map((cell: any) => {
              const isNriCell = cell.column.id === 'unitNri';

              const shouldHighlightBlue =
                !isSubtotalRow(row) &&
                (cell.column.id === 'unitNri' ||
                  cell.column.id === 'royaltyRate') &&
                row.original &&
                !!row.original.defaultRoyaltyUsed;

              const shouldHighlightGreen =
                cell.column.id === 'participationFactor' &&
                row.original &&
                !!row.original.participationFactorOverridden;

              return (
                <td
                  key={cell.id}
                  style={{
                    width: cell.column.getSize(),
                    overflow: isNriCell ? 'visible' : 'hidden',
                  }}
                  className={`
                    ${isSubtotalRow(row) ? styles.subtotalCell : ''}
                    ${isNriCell ? styles.nriCell : ''}
                    ${shouldHighlightBlue ? styles.highlightBlue : ''}
                    ${shouldHighlightGreen ? styles.highlightGreen : ''}
                  `}
                >
                  {isNriCell ? (
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                  ) : (
                    <div>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  )}
                </td>
              );
            })}
          </tr>
        );
      })}

      {getSubtotalRow && table.getRowModel().rows.length > 0 && (
        <tr
          key="subtotal-row"
          className={styles.subtotalRow}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '48px',
            transform: `translateY(${virtualizer.getTotalSize()}px)`,
            background: '#ffffff',
            borderTop: '1px solid #e0e0e0',
            fontWeight: 'bold',
          }}
        >
          {table
            .getVisibleLeafColumns()
            .map(
              (
                column: { id: string; getSize: () => number },
                index: number
              ) => {
                const subtotalRow = getSubtotalRow(
                  table.getFilteredRowModel().rows
                );
                return (
                  <td
                    key={`subtotal-${column.id}`}
                    style={{
                      width: column.getSize(),
                    }}
                    className={styles.subtotalCell}
                  >
                    {index === 0 ? 'Subtotal' : subtotalRow[column.id] || null}
                  </td>
                );
              }
            )}
        </tr>
      )}
    </tbody>
  );
};
